/**
 * DeleteScopeModal - Choose delete scope for recurring events
 *
 * When deleting an event that belongs to a training group, prompts user to choose:
 * - "This event only" - Delete just this instance
 * - "This and future events" - Delete this event and all future events in the series
 *
 * Issue #1203
 */

import { useState, useEffect, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import Button from './Button';
import type { EditScope } from '../lib/api/events';

// Reuse EditScope type for delete scope (same values)
export type DeleteScope = EditScope;

interface DeleteScopeModalProps {
  isOpen: boolean;
  onConfirm: (scope: DeleteScope) => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

export default function DeleteScopeModal({
  isOpen,
  onConfirm,
  onCancel,
  isDeleting = false,
}: DeleteScopeModalProps) {
  const [selectedScope, setSelectedScope] = useState<DeleteScope>('this_only');
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const dialogRef = useRef<HTMLDivElement>(null);

  // React docs "adjust state on prop change" — reset selection on open transition.
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) {
      setSelectedScope('this_only');
    }
  }

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel, isDeleting]);

  // Focus trap
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const firstButton = dialogRef.current.querySelector('button');
      firstButton?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(selectedScope);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isDeleting) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-scope-title"
        aria-describedby="delete-scope-description"
        className="bg-oro-surface border border-oro-border rounded-lg max-w-sm w-full p-6 shadow-card animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-oro-error/10 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-oro-error" />
          </div>
          <div>
            <h3
              id="delete-scope-title"
              className="text-lg font-semibold text-text-primary"
            >
              Delete Recurring Event
            </h3>
            <p
              id="delete-scope-description"
              className="text-text-muted text-sm"
            >
              Which events should be deleted?
            </p>
          </div>
        </div>

        {/* Options */}
        <div className="space-y-3 mb-6">
          {/* This event only */}
          <button
            type="button"
            onClick={() => setSelectedScope('this_only')}
            disabled={isDeleting}
            className={`w-full p-4 rounded-lg border text-left transition-colors ${
              selectedScope === 'this_only'
                ? 'border-oro-error bg-oro-error/5'
                : 'border-oro-border hover:border-oro-border-hover'
            } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                selectedScope === 'this_only'
                  ? 'border-oro-error'
                  : 'border-text-muted'
              }`}>
                {selectedScope === 'this_only' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-oro-error" />
                )}
              </div>
              <div>
                <p className="font-medium text-text-primary">This event only</p>
                <p className="text-sm text-text-secondary mt-1">
                  Delete only this event. Other events in the series will remain.
                </p>
              </div>
            </div>
          </button>

          {/* This and future events */}
          <button
            type="button"
            onClick={() => setSelectedScope('this_and_future')}
            disabled={isDeleting}
            className={`w-full p-4 rounded-lg border text-left transition-colors ${
              selectedScope === 'this_and_future'
                ? 'border-oro-error bg-oro-error/5'
                : 'border-oro-border hover:border-oro-border-hover'
            } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                selectedScope === 'this_and_future'
                  ? 'border-oro-error'
                  : 'border-text-muted'
              }`}>
                {selectedScope === 'this_and_future' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-oro-error" />
                )}
              </div>
              <div>
                <p className="font-medium text-text-primary">This and future events</p>
                <p className="text-sm text-text-secondary mt-1">
                  Delete this event and all future events in the series. Past events will remain.
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Warning */}
        <div className="mb-6 p-3 rounded-lg bg-oro-error/10 border border-oro-error/20">
          <p className="text-sm text-oro-error">
            This action cannot be undone. All event data and check-ins will be permanently removed.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1 bg-oro-error hover:bg-oro-error/80 border-oro-error"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
